import { Stack, TableBody, TableHead, TableRow } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { useSeoPages } from 'hooks/api'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable } from '../shared'
import { fmt, fmtPct, fmtPos, sourceText } from './seoModel'

/**
 * Which pages earn search traffic AND turn it into leads. Search Console (clicks,
 * position) joined by path to the website funnel (visitors, forms, signups, clients) —
 * the same figures as the Website tab, counted per landing page. Counts only.
 */
export const SeoPagesCard = ({ range }: { range: { since: string; until: string } }) => {
  const { data, isLoading, error } = useSeoPages(range)
  const notices = data
    ? [
        sourceText('Search Console', data.sources.gsc),
        sourceText('Vercel', data.sources.vercel),
        sourceText('PostHog', data.sources.posthog),
      ].filter((t): t is string => !!t)
    : []

  return (
    <Card title="Pages to leads">
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <Skeleton height={14} lines={8} />
        ) : error || !data ? (
          <SectionNote>Could not load pages: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <>
            {notices.map((n) => (
              <Notice key={n} severity="warning">
                {n}
              </Notice>
            ))}
            <SectionNote>
              Ranked by leads (signups, then clients, then forms), then clicks. Search columns are
              Google&rsquo;s; visitor and form columns are every visitor (Vercel); signups and
              clients are attributed by landing page for visitors with analytics on, so they
              undercount. A dash means the source has no figure, not zero.
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Page</HeadCell>
                  <HeadCell>Top query</HeadCell>
                  <HeadCell align="right">Clicks</HeadCell>
                  <HeadCell align="right">Impressions</HeadCell>
                  <HeadCell align="right">CTR</HeadCell>
                  <HeadCell align="right">Position</HeadCell>
                  <HeadCell align="right">Visitors</HeadCell>
                  <HeadCell align="right">Forms</HeadCell>
                  <HeadCell align="right">Signups</HeadCell>
                  <HeadCell align="right">Clients</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 ? (
                  <EmptyRow
                    colSpan={10}
                    text="No page has search or visitor data for this range."
                  />
                ) : (
                  data.rows.slice(0, 50).map((r) => (
                    <TableRow key={r.path}>
                      <Cell strong>{r.path}</Cell>
                      <Cell>{r.topQuery ?? '—'}</Cell>
                      <Cell align="right">{fmt(r.clicks)}</Cell>
                      <Cell align="right">{fmt(r.impressions)}</Cell>
                      <Cell align="right">{fmtPct(r.ctr)}</Cell>
                      <Cell align="right">{fmtPos(r.position)}</Cell>
                      <Cell align="right">{fmt(r.visitors)}</Cell>
                      <Cell align="right">{fmt(r.formSubmitted)}</Cell>
                      <Cell align="right" strong>
                        {fmt(r.signups)}
                      </Cell>
                      <Cell align="right">{fmt(r.clients)}</Cell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StatTable>
          </>
        )}
      </Stack>
    </Card>
  )
}
